import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import Icon from '../../../Icons'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import EditorText from '../../../EditorText/EditorText'
import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'
import RadioField from '../../../FormLegacy/FormFieldsV2/RadioField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import { updateBuildingActivity } from '../../../../reducers/loyaltyReducer'
import { getCheckboxHandler, getNamedHandler } from '../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

const MAX_AMOUNT_VALUE = 5000
const POINTS_MULTIPLIER = 100
const USAGE_LIMIT_TIMES = 1

const RewardsEditActivityModal = ({
  onClose = () => {},
  updateBuildingActivity = () => {},
  activity,
}) => {
  const { t } = useTranslation(['rewards-loyalty', 'common'])
  const {
    name,
    description,
    points,
    requiresApproval,
    isFeatured,
    verificationMethod,
    expirationDate,
  } = activity

  const [formState, setFormState] = useState({
    name,
    description,
    amount: points / POINTS_MULTIPLIER,
    requiresApproval,
    isFeatured,
    verificationMethod,
    expirationDate,
  })
  const [isLoading, setIsLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const canUpdate =
    name !== formState.name ||
    description !== formState.description ||
    points !== formState.amount * POINTS_MULTIPLIER ||
    requiresApproval !== formState.requiresApproval ||
    isFeatured !== formState.isFeatured ||
    verificationMethod !== formState.verificationMethod ||
    expirationDate !== formState.expirationDate

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const onHandleNameField = getNamedHandler(handleFieldChange)
  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)

  const handleChangeAmount = (e) => {
    const max = MAX_AMOUNT_VALUE
    const min = 0
    const { value, name } = e.target
    let num = parseInt(value)
    if (num > max) num = max
    if (num < min) num = min
    return handleFieldChange({ target: { name, value: num } })
  }

  const onSubmit = async () => {
    try {
      setIsLoading(true)
      const data = {
        name: formState.name,
        description: formState.description,
        points: formState.amount * POINTS_MULTIPLIER,
        requiresApproval: formState.requiresApproval,
        isFeatured: formState.isFeatured,
        verificationMethod: formState.verificationMethod,
        expirationDate: formState.expirationDate,
      }
      await updateBuildingActivity(activity.id, data)
      displaySnackbar(t('rewards-loyalty:activityForm.changesSavedSuccess'))
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(t('rewards-loyalty:editActivity.saveError'), true)
    } finally {
      setIsLoading(false)
      onClose()
    }
  }

  return (
    <ModalContainer
      additionalClass={'rewards-modal'}
      onClose={onClose}
      headerTitle={t('rewards-loyalty:editActivity.headerTitle')}
      headerIcon={<Icon icon="walk" viewBox="0 0 18 21" svgProps={{ fillRule: 'evenodd' }} />}
    >
      <div
        className={`rewards-modal-content rewards-base-view activity-edit ${
          isLoading ? 'is-loading' : ''
        }`}
      >
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <div className="input-label">{t('rewards-loyalty:activityForm.activityNameLabel')}</div>
            <TextField
              name="name"
              onChange={handleFieldChange}
              value={formState.name}
              placeholder={t('rewards-loyalty:activityForm.enterActivityNamePlaceholder')}
              style={{ width: '100%' }}
              minLength="1"
              maxLength="30"
            />
            <span className="input-desc-label">
              {t('rewards-loyalty:activityForm.maxLength30Info')}
            </span>
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('rewards-loyalty:activityForm.activityDescriptionLabel')}
            </div>
            <EditorText
              value={formState.description}
              onChange={(content, text) =>
                onHandleNameField(text.trim() ? content : undefined, 'description')
              }
              placeholder={t('rewards-loyalty:editActivity.activityDescriptionPlaceholder')}
            />
          </div>
        </div>
        <div className="rewards-modal-input-row" style={{ display: 'flex', gap: 8 }}>
          <div className="input-container" style={{ width: '50%' }}>
            <div className={`input-label`}>
              {t('rewards-loyalty:activityForm.dollarValueLabel')}
            </div>
            <TextField
              name="amount"
              maxLength="10"
              onChange={handleChangeAmount}
              value={formState.amount}
              placeholder={t('rewards-loyalty:activityForm.enterAmountPlaceholder')}
              style={{ width: '100%' }}
              type="number"
              leftIcon={
                <Icon
                  icon="dollar"
                  fill="none"
                  svgProps={{
                    stroke: '#3C3C3C',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  }}
                />
              }
            />
          </div>
          <div className="input-container" style={{ width: '50%' }}>
            <div className={`input-label disabled`}>
              {t('rewards-loyalty:activityForm.pointsValueLabel')}
            </div>
            <TextField
              name="amount"
              value={(formState.amount || 0) * POINTS_MULTIPLIER}
              style={{ width: '100%' }}
              disabled
            />
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <div className="input-label">{t('rewards-loyalty:activityForm.usageLimitLabel')}</div>
            <TextField value={USAGE_LIMIT_TIMES} style={{ width: '100%' }} disabled />
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <ToggleField
              label={t('rewards-loyalty:activityForm.requiresApprovalLabel')}
              labelOnRight
              name="requiresApproval"
              onChange={() => {
                if (formState.requiresApproval) {
                  onHandleNameField('none', 'verificationMethod')
                }
                onHandleNameField(!formState.requiresApproval, 'requiresApproval')
              }}
              value={formState.requiresApproval}
            />
          </div>
        </div>
        {formState.requiresApproval && (
          <div className="rewards-modal-input-row">
            <div className="input-container">
              <div className="input-label">
                {t('rewards-loyalty:activityForm.activityVerificationMethodLabel')}
              </div>
              <div className="radio-group-row">
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'image'}
                  label={t('rewards-loyalty:activityForm.photosScreenshotsLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationMethod')}
                  checked={formState.verificationMethod === 'image'}
                />
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'link'}
                  label={t('rewards-loyalty:activityForm.linkLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationMethod')}
                  checked={formState.verificationMethod === 'link'}
                />
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'text'}
                  label={t('rewards-loyalty:activityForm.textInputLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationMethod')}
                  checked={formState.verificationMethod === 'text'}
                />
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'none'}
                  label={t('rewards-loyalty:activityForm.noneLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationMethod')}
                  checked={formState.verificationMethod === 'none'}
                />
              </div>
            </div>
          </div>
        )}
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('rewards-loyalty:activityForm.expirationDateLabel')}{' '}
              <span className="input-desc-label">
                ({t('rewards-loyalty:activityForm.optionalSuffix')})
              </span>
            </div>
            <DateField
              name="expirationDate"
              placeholderText="(MM/DD/YYYY)"
              autoComplete="off"
              dateFormat="MM/dd/yyyy"
              minDate={new Date()}
              value={formState.expirationDate}
              onChange={(e) =>
                onHandleNameField(new Date(e.target.value).getTime(), 'expirationDate')
              }
              onChangeRaw={(e) => e.preventDefault()}
            />
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <CheckboxField
              name="isFeatured"
              value={formState.isFeatured}
              onChange={handleCheckboxChange}
              label={
                <React.Fragment>
                  {t('rewards-loyalty:activityForm.makeActivityFeaturedLabel')}
                  <div data-tip data-for="pause-points-tip">
                    <Icon icon="info" viewBox="0 0 25 25" />
                    <ReactTooltip className="tooltip" id="pause-points-tip" multiline place="top">
                      {t('rewards-loyalty:activityForm.featuredActivityTooltip')}
                    </ReactTooltip>
                  </div>
                </React.Fragment>
              }
            />
          </div>
        </div>
      </div>
      <div className="rewards-modal-actions">
        <button className="button button-gray" onClick={onClose}>
          {t('common:actions.back')}
        </button>
        <button
          className="button button-primary auto-width"
          disabled={!canUpdate}
          onClick={onSubmit}
        >
          {t('rewards-loyalty:editActivity.saveChangesButton')}
        </button>
      </div>
    </ModalContainer>
  )
}

RewardsEditActivityModal.propTypes = {
  onClose: PropTypes.func,
  updateBuildingActivity: PropTypes.func,
  building: PropTypes.object,
  activity: PropTypes.object,
}

export default connect(null, { updateBuildingActivity })(RewardsEditActivityModal)
